/**
 * The sound an incoming call makes.
 *
 * Synthesised rather than played from a file, and that is the only unusual
 * decision here. A ringtone is two sine tones and an envelope; shipping it as
 * an asset means a binary in the repository, a second format for Safari, a
 * fetch that can fail and a decode that can fail — all of it in the one code
 * path that has to work on a phone that has been asleep for six hours. Twenty
 * lines of oscillator have none of those failure modes.
 *
 * Two rising blips rather than one long tone: a single sustained note is what
 * an alarm sounds like, and this has to read as somebody calling.
 *
 * The context is passed in rather than made here, so the scheduling below is
 * testable without an audio device. `ringtoneContext` is the other half.
 */

/** Just the part of an AudioContext this needs, so a test can be an object. */
export interface RingtoneContext {
  readonly currentTime: number;
  readonly destination: AudioNode;
  resume(): Promise<void>;
  createOscillator(): OscillatorNode;
  createGain(): GainNode;
}

const LOW_HZ = 440;
/** A major third up. Two notes that belong together, rather than a siren. */
const HIGH_HZ = 554.37;
const BLIP_S = 0.4;
const GAP_S = 0.16;
/** Long enough that the silence between rings is part of the sound. */
const PERIOD_MS = 3000;
/**
 * Quiet. This plays on a phone held near a face, and the far end's voice a
 * second later is the thing that has to be the loudest part of a call.
 */
const PEAK = 0.16;
/** Short enough to be inaudible, long enough that the edge is not a click. */
const EDGE_S = 0.02;

function blip(gain: GainNode, oscillator: OscillatorNode, at: number, hz: number): void {
  oscillator.frequency.setValueAtTime(hz, at);
  // Ramped rather than stepped at both ends: a gain that jumps from zero is a
  // click, and a click on every ring is most of what makes a synthesised tone
  // sound like a bug.
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(PEAK, at + EDGE_S);
  gain.gain.setValueAtTime(PEAK, at + BLIP_S - EDGE_S);
  gain.gain.linearRampToValueAtTime(0, at + BLIP_S);
}

/** Starts ringing. The returned function stops it, and may be called twice. */
export function startRingtone(context: RingtoneContext): () => void {
  // A context built before any gesture starts suspended. Inside the native
  // shell it resumes (Capacitor clears WKWebView's user-action requirement);
  // in a browser tab nobody has touched, it does not, and the call is silent
  // rather than broken.
  void context.resume().catch(() => {});

  const gain = context.createGain();
  gain.gain.value = 0;
  gain.connect(context.destination);

  const oscillator = context.createOscillator();
  oscillator.type = 'sine';
  oscillator.connect(gain);
  oscillator.start();

  const ring = (): void => {
    const at = context.currentTime;
    blip(gain, oscillator, at, LOW_HZ);
    blip(gain, oscillator, at + BLIP_S + GAP_S, HIGH_HZ);
  };
  ring();
  const timer = setInterval(ring, PERIOD_MS);

  let stopped = false;
  return () => {
    // Answering and the call ending can both arrive, and a second oscillator
    // stop throws.
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    gain.gain.cancelScheduledValues(context.currentTime);
    gain.gain.setValueAtTime(0, context.currentTime);
    oscillator.stop();
    oscillator.disconnect();
    gain.disconnect();
  };
}

let shared: AudioContext | null = null;

/**
 * The app's one AudioContext, or null where there is none to have.
 *
 * Kept rather than made per call: browsers cap how many a page may have, and a
 * context created while a page has never been touched stays suspended — so the
 * one that has been resumed once is worth more than a fresh one.
 */
export function ringtoneContext(): RingtoneContext | null {
  if (shared) return shared;
  const constructor =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!constructor) return null;
  shared = new constructor();
  return shared;
}
